import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import { InputText, Label, Dropdown } from "../../../../components/Inputs";
import RoleForm from "./RoleForm";
import { createSortByLabel } from "../../../../components/Utils";
import { connect } from "react-redux";
import { loadWorkerCompanies } from "../../actions";
import { PermissionResource, Resources } from "../../../Permissions";

const findAttrByAttr = (list, value, listAttr = "id", retAttr = "name") => {
  let found = list.find((item) => item[listAttr] === value);
  if (found) {
    found = found[retAttr];
  }
  return found;
};

const getInitialState = (props) => {
  const model = props.model || {};
  let id = model.id || "";
  console.log("CompanyDataForm: getInitialState()", model, "props:", props);
  let contractGiver = model.contractGiver || "";
  let contractGiverId = props.workerKdl || model.contractGiverId || 0;
  if (contractGiverId !== 0) {
    const found = (props.kdlCompanyList || []).find(
      (item) => item.id === props.workerKdl,
    );
    if (found) {
      contractGiver = found.name;
    }
  } else if (contractGiver !== "") {
    // find contract giver id by name if not set but name is set
    const found = (props.kdlCompanyList || []).find(
      (item) => item.name === contractGiver,
    );
    if (found) {
      contractGiverId = found.id;
    }
  }
  return {
    id,
    contractGiver,
    contractGiverId,
    telephoneNumber: model.telephoneNumber || "",
    organisationUnit: model.organisationUnit || "",
    isContractor: !!(`${id}` !== `${contractGiverId}`),
    roles: model.roles || [],
    roleList: props.roleList || [],
    error: {
      id: null,
      contractGiverId: null,
      roles: null,
    },
  };
};

const mapStateToProps = (state) => ({
  workerKdl: state.worker.workerKDL,
  companyList: state.worker.workerCompanies,
});
const mapDispatchToProps = (dispatch) => ({
  getWorkerCompanies: (...args) => dispatch(loadWorkerCompanies(...args)),
});

const CompanyDataForm = forwardRef(function CompanyDataForm(props, ref) {
  const {
    className = "",
    namePrefix = "",
    model,
    companyList = [],
    kdlCompanyList = [],
    extendedRoleList = [],
    disabled,
    isNGUMActive,
  } = props;

  const [state, setState] = useState(() => getInitialState(props));

  const extendedRolesRef = useRef(null);
  // Keep refs current every render so imperative handles never read stale values
  const stateRef = useRef(state);
  const propsRef = useRef(props);
  stateRef.current = state;
  propsRef.current = props;

  const generateRoleData = (oldRoles) => {
    const roleList = propsRef.current.roleList.filter(() => true);
    const roles = oldRoles.filter((roleName) => {
      const pos = roleList.findIndex((item) => item.name === roleName);
      return pos >= 0;
    });
    return { roles, roleList };
  };

  const handleSetContractGiverId = (value, companyId = 0) => {
    if (value && value !== 0) {
      setState((prev) => ({
        ...prev,
        contractGiverId: value,
        id: companyId,
        error: {
          ...prev.error,
          contractGiverId: null,
        },
      }));
      propsRef.current.getWorkerCompanies(value);
    }
  };

  const handleSetCompany = (value) => {
    const { roles: oldRoles } = stateRef.current;
    const { roles, roleList } = generateRoleData(oldRoles);
    const newState = {
      id: value,
      roles,
      roleList,
      error: {
        ...stateRef.current.error,
        id: null,
      },
    };
    const company = propsRef.current.companyList.find(
      (item) => item.id === value,
    );
    newState.isContractor = !!(company && company.type_id > 3);
    setState((prev) => ({ ...prev, ...newState }));
  };

  // componentDidMount: resolve initial contractGiverId or company
  useEffect(() => {
    const { id, contractGiverId, contractGiver } = stateRef.current;
    if (contractGiver !== "" && contractGiverId === 0) {
      const found = propsRef.current.kdlCompanyList
        ? propsRef.current.kdlCompanyList.find(
            (item) => item.name === contractGiver,
          )
        : null;
      if (found) {
        handleSetContractGiverId(found.id, id);
      }
    } else if (id !== 0) {
      handleSetCompany(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getModel: (noValidate) => {
        const { /* isMain,  */ companyList, kdlCompanyList } = propsRef.current;
        const {
          error: currentError,
          isContractor,
          roleList: rl,
          ...values
        } = stateRef.current;
        const error = { ...currentError };
        let isValid = true;
        console.log("CompanyDataForm: getModel()", values, noValidate);

        // required fields
        // 1. contract giver
        if (values.contractGiverId === "" || values.contractGiverId === 0) {
          error.contractGiverId = true;
          isValid = false;
        }

        // 2. company (if main company, otherwise optional)
        if (values.id === "" || values.id === 0) {
          error.id = true;
          isValid = false;
        }

        if (!noValidate) {
          setState((prev) => ({ ...prev, error }));
          if (!isValid) return null;
        }

        const model = { ...values };
        // extended roles
        model.extendedRoles = extendedRolesRef.current
          ? extendedRolesRef.current.getModel()
          : [];
        // original id and contract giver
        const oldModel = propsRef.current.model || {};
        model.oldName = oldModel.name;
        model.oldId = oldModel.id;
        model.oldContractGiver = oldModel.contractGiver;
        model.oldContractGiverId =
          oldModel.contractGiverId ||
          findAttrByAttr(kdlCompanyList, oldModel.contractGiver, "name", "id");
        // set company name
        model.name = findAttrByAttr(companyList, model.id, "id", "name");
        model.contractGiver = findAttrByAttr(
          kdlCompanyList,
          model.contractGiverId,
          "id",
          "name",
        );
        // console.log("<< CompanyData:", model);
        return model;
      },
      getCompanyData: () => {
        const { isContractor, roleList: rl } = stateRef.current;
        const contractGiver = findAttrByAttr(
          propsRef.current.kdlCompanyList,
          stateRef.current.contractGiverId,
        );
        return { isContractor, roleList: rl, contractGiver };
      },
    }),
    [],
  ); // empty deps — all reads go through refs

  const { id, contractGiverId, isContractor, organisationUnit, error } = state;

  const KDLCompanyOptions = kdlCompanyList
    .filter((company) => company.status_id !== 6)
    .map((company) => ({
      label: company.name,
      value: company.id,
    }))
    .sort(createSortByLabel());
  const CompanyOptions = companyList
    .map((company) => ({
      label: company.name,
      value: company.id,
    }))
    .sort(createSortByLabel());

  // contract giver (KDL)
  console.log(
    "CompanyDataForm()",
    contractGiverId,
    "KDLCompanyOptions:",
    KDLCompanyOptions,
  );
  const contractGiverMarkup = (
    <Dropdown
      id={`${namePrefix}contractGiver`}
      className="w3-block w3-border"
      value={contractGiverId}
      placeholder="Bitte auswählen"
      options={KDLCompanyOptions}
      onChange={(event) => handleSetContractGiverId(event.value)}
      showClear
      disabled={disabled}
    />
  );

  // company (Beschäftigungsfirma)
  console.log("CompanyDataForm()", id, "CompanyOptions:", CompanyOptions);
  const companyMarkup = (
    <Dropdown
      id={`${namePrefix}name`}
      className="w3-block w3-border"
      value={id}
      placeholder="Bitte auswählen"
      options={CompanyOptions}
      onChange={(event) => handleSetCompany(event.value)}
      showClear
      disabled={disabled}
    />
  );

  return (
    <div className={className}>
      <div className="w3-row neg-margin-lr">
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${
            error.contractGiverId ? " error-group" : ""
          }`}
        >
          <Label htmlFor={`${namePrefix}contractGiver`}>
            {`KDL/DL`}
            <span>*</span>
          </Label>
          {contractGiverMarkup}
        </div>
        <div className="w3-col s4 pad-lr ">
          <div
            className={`pad-top form-group-item ${
              error.id ? " error-group" : ""
            }`}
          >
            <Label htmlFor={`${namePrefix}name`}>
              {`Beschäftigungsfirma`}
              <span>*</span>
            </Label>
            {companyMarkup}
          </div>
          {/* <div className="pad-top" style={{display:"flex", gap:"4px"}}>
            <Label className="" style={{width:"105px",flexShrink:"0"}}>{`VF Org-Einheit`}</Label>
            <div className="" style={{flexGrow: "1"}}>
              <div className="text-one-line" style={{overflow:"auto"}}>
                {model.organisationUnit || ""}
              </div>
            </div>
          </div> */}
        </div>
        <PermissionResource
          resource={
            Resources.Organisation.WorkerWizard.BasicInfoOrganisationUnit
          }
        >
          <OrganisationUnitForm
            className={`w3-col s4 pad-lr pad-top form-group-item`}
            isNGUMActive={isNGUMActive}
            value={organisationUnit}
            onChange={(e) => {
              const value = e.target.value;
              setState((prev) => ({ ...prev, organisationUnit: value }));
            }}
          />
        </PermissionResource>
        {/* <div className={`w3-col s4 pad-lr pad-top form-group-item`}>
          <Label>
            {`Vodafone Organisationseinheit`}
          </Label>
          <InputText
            className="w3-block w3-border pad w3-round"
            value={organisationUnit }
            readOnly={isNGUMActive}
          />
        </div> */}
      </div>
      <RoleForm
        ref={extendedRolesRef}
        id="extendedRoles"
        className="pad-top"
        title="Aufgabenbereiche"
        model={model ? model.extendedRoles || [] : []}
        roleList={extendedRoleList}
        disabled={disabled}
        isContractor={isContractor}
      />
    </div>
  );
});

CompanyDataForm.propTypes = {
  className: PropTypes.string,
  namePrefix: PropTypes.string,
  model: PropTypes.shape({}),
  workerKdl: PropTypes.number,
  getWorkerCompanies: PropTypes.func.isRequired,
  companyLis: PropTypes.array,
  isMain: PropTypes.bool,
  company: PropTypes.shape({}),
  // companyList: PropTypes.array,
  kdlCompanyList: PropTypes.array,
  userCompany: PropTypes.shape({}),
  roleList: PropTypes.array,
  extendedRoleList: PropTypes.array,
  userExists: PropTypes.bool,
  canReadAdminData: PropTypes.bool,
  disabled: PropTypes.bool,
  isNGUMActive: PropTypes.bool,
};

CompanyDataForm.defaultProps = {
  className: "",
  namePrefix: "",
  model: {
    id: 0,
    contractGiver: "",
    contractGiverId: 0,
    telephoneNumber: "",
    roles: [],
    extendedRoles: [],
  },
  companyList: [],
  kdlCompanyList: [],
  roleList: [],
  extendedRoleList: [],
  disabled: false,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(CompanyDataForm);

const OrganisationUnitForm = forwardRef(
  function OrganisationUnitForm(props, ref) {
    const { className = "", isNGUMActive, value, onChange, disabled } = props;
    const readOnly = isNGUMActive ? true : disabled;
    // console.log("OrganisationUnitForm()", { value, isNGUMActive, disabled, readOnly });
    return (
      <div className={`${className}`}>
        <Label>{`Vodafone Organisationseinheit`}</Label>
        <InputText
          ref={ref}
          className={`w3-block w3-border pad w3-round`}
          value={value}
          disabled={readOnly}
          onChange={onChange}
        />
      </div>
    );
  },
);

OrganisationUnitForm.propTypes = {
  className: PropTypes.string,
  isNGUMActive: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

OrganisationUnitForm.defaultProps = {
  className: "",
  isNGUMActive: false,
  value: "",
  onChange: () => {},
};
